import * as React from "react"
import { X, ImageIcon, Plus } from "lucide-react"
import { cn } from "../../../lib/utils"
import { Button } from "../Button/Button"
import { AppImage } from "../AppImage/AppImage"

export interface ImageGalleryProps {
    images: (File | string)[]
    onChange: (images: (File | string)[]) => void
    maxImages?: number
    className?: string
}

export const ImageGallery = ({
    images = [],
    onChange,
    maxImages = 10,
    className
}: ImageGalleryProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        const remainingSlots = maxImages - images.length
        const filesToAdd = files.slice(0, remainingSlots)

        onChange([...images, ...filesToAdd])

        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }

    const removeImage = (index: number) => {
        const newImages = [...images]
        newImages.splice(index, 1)
        onChange(newImages)
    }

    return (
        <div className={cn("space-y-4", className)}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {images.map((image, index) => (
                    <div
                        key={index}
                        className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-border/50 group bg-slate-50"
                    >
                        <PreviewImage
                            image={image}
                            alt={`Product ${index + 1}`}
                            className="rounded-sm"
                        />
                        <div className="absolute top-2 right-2 flex items-center justify-center opacity-100 transition-opacity">
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="size-7 p-0 rounded-full shadow-lg border-2 border-white"
                                onClick={() => removeImage(index)}
                            >
                                <X className="size-3.5" />
                            </Button>
                        </div>
                        {index === 0 && (
                            <div className="absolute top-2 left-2 px-2 py-0.5 bg-secondary text-white text-[10px] font-bold rounded-full shadow-sm">
                                MAIN
                            </div>
                        )}
                    </div>
                ))}

                {images.length < maxImages && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="aspect-[4/3] rounded-2xl border-2 border-dashed border-border/60 hover:border-secondary/50 hover:bg-secondary/5 transition-all flex flex-col items-center justify-center gap-2 group"
                    >
                        <div className="size-10 rounded-full bg-secondary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Plus className="size-5 text-secondary" />
                        </div>
                        <span className="text-[10px] font-bold text-muted-foreground group-hover:text-secondary uppercase tracking-wider">
                            Add Image
                        </span>
                    </button>
                )}
            </div>

            <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                ref={inputRef}
                onChange={handleFileChange}
            />

            <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                <ImageIcon className="size-3.5" />
                <span>Upload up to {maxImages} images. The first image will be used as the main cover.</span>
            </div>
        </div>
    )
}
const PreviewImage = ({ image, alt, className }: { image: File | string, alt: string, className: string }) => {
    const [previewUrl, setPreviewUrl] = React.useState<string>("")

    React.useEffect(() => {
        if (typeof image === "string") {
            setPreviewUrl(image)
            return
        }

        if (image instanceof Blob) {
            const url = URL.createObjectURL(image)
            setPreviewUrl(url)
            return () => URL.revokeObjectURL(url)
        }

        setPreviewUrl("")
    }, [image])

    if (!previewUrl) {
        return (
            <div className={cn("w-full h-full flex flex-col items-center justify-center bg-slate-100 text-muted-foreground gap-1", className)}>
                <ImageIcon className="size-8 opacity-20" />
                <span className="text-[8px] uppercase font-bold opacity-40">Invalid File</span>
            </div>
        )
    }

    return (
        <AppImage
            src={previewUrl}
            alt={alt}
            className={className}
        />
    )
}
